// src/pages/Gifts.jsx
import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Search, Gift, ShoppingCart, MessageCircle } from "lucide-react";
import api from "../api/api";
import { toast } from "react-toastify";
import { useCart } from "../context/CartContext";
import GiftCategoryRow from "../components/gifts/GiftCategoryRow";
import GiftCard from "../components/gifts/GiftCard";
import GiftDetailModal from "../components/gifts/GiftDetailModal";
import CuratedCollectionRail from "../components/gifts/CuratedCollectionRail";
import { GIFT_CATEGORIES } from "../constants/giftCategories";

const WHATSAPP_NUMBER = "2348079208230";

export default function Gifts() {
  const { cartCount } = useCart();

  // Home view (category rows)
  const [sections, setSections] = useState([]);
  const [homeLoading, setHomeLoading] = useState(true);

  // Filtered view (flat grid) — used when a category chip or search is active
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [gifts, setGifts] = useState([]);
  const [gridLoading, setGridLoading] = useState(false);
  const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1 });

  const [openGift, setOpenGift] = useState(null);

  const abortRef = useRef(null);
  const debounceRef = useRef(null);

  const isFiltered = activeCategory !== "all" || searchQuery.trim() !== "";

  // ── Load home sections once ──────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      setHomeLoading(true);
      try {
        const res = await api.get("/gifts/home");
        if (res.data.success) setSections(res.data.sections);
      } catch {
        toast.error("Failed to load gifts. Check your connection and try again.");
      } finally {
        setHomeLoading(false);
      }
    };
    load();
  }, []);

  // ── Live search — fires 350ms after the user stops typing, no button needed
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setSearchQuery(searchInput), 200);
    return () => clearTimeout(debounceRef.current);
  }, [searchInput]);

  // ── Fetch flat grid whenever category or search changes ─────────────────
  const fetchGrid = async (page = 1) => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setGridLoading(true);
    try {
      const params = new URLSearchParams({ page });
      if (activeCategory !== "all") params.append("category", activeCategory);
      if (searchQuery.trim()) params.append("search", searchQuery.trim());
      const res = await api.get(`/gifts?${params}`, { signal: controller.signal });
      if (res.data.success) {
        setGifts(res.data.gifts);
        setPagination(res.data.pagination);
      }
    } catch (err) {
      if (err.code === "ERR_CANCELED") return;
      toast.error("Failed to load gifts");
    } finally {
      if (abortRef.current === controller) setGridLoading(false);
    }
  };

  useEffect(() => {
    if (isFiltered) fetchGrid(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategory, searchQuery]);

  const whatsappHref = (giftTitle) => {
    const text = giftTitle
      ? `Hi, I'm looking for "${giftTitle}" but it's not on the Send Gifts page. Can you help me get this gift?`
      : `Hi, I'm looking for a gift that's not on the Send Gifts page. Can you help me?`;
    return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`;
  };

  return (
    <div className="animate-fadeIn">
      {/* ── Editorial hero ── */}
      <div className="relative pt-1 pb-5 mb-5 border-b border-gray-100 dark:border-gray-800 text-center">
        <Link
          to="/dashboard/cart"
          className="absolute top-0 right-0 shrink-0 w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center bg-white dark:bg-[#0f172a] border border-gray-200 dark:border-gray-800 rounded-xl hover:border-purple-300 dark:hover:border-purple-700 transition"
        >
          <ShoppingCart className="w-4 h-4 sm:w-[18px] sm:h-[18px] text-gray-700 dark:text-gray-300" />
          {cartCount > 0 && (
            <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center bg-purple-600 text-white text-[10px] font-bold rounded-full">
              {cartCount > 99 ? "99+" : cartCount}
            </span>
          )}
        </Link>

        <p className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.14em] text-purple-600 dark:text-purple-400 mb-2">
          Send Gifts
        </p>
        <h1 className="font-serif text-xl sm:text-3xl font-normal text-gray-900 dark:text-white leading-snug mb-2 px-8 sm:px-0">
          Something they'll actually remember
        </h1>
        <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 max-w-md mx-auto">
          Surprise the people you love, anywhere in the world — same day where it matters most.
        </p>

        {/* Live search — Google-style rounded bar, no Go button needed */}
        <div className="mt-4 max-w-lg mx-auto">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search all gifts..."
              className="w-full pl-11 pr-4 py-3 text-sm bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-full shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900 dark:text-white placeholder-gray-400 transition"
            />
          </div>
        </div>
      </div>

      {/* Category filter chips */}
      <div className="flex gap-2 overflow-x-auto pb-1 mb-5 -mx-4 px-4 sm:mx-0 sm:px-0">
        <button
          onClick={() => setActiveCategory("all")}
          className={`flex-shrink-0 px-4 py-1.5 rounded-full text-xs sm:text-sm font-semibold transition ${
            activeCategory === "all"
              ? "bg-purple-600 text-white"
              : "bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:border-purple-300"
          }`}
        >
          All
        </button>
        {GIFT_CATEGORIES.map((c) => (
          <button
            key={c.key}
            onClick={() => setActiveCategory(c.key)}
            className={`flex-shrink-0 px-4 py-1.5 rounded-full text-xs sm:text-sm font-semibold transition ${
              activeCategory === c.key
                ? "bg-purple-600 text-white"
                : "bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:border-purple-300"
            }`}
          >
            {c.short}
          </button>
        ))}
      </div>

      {!isFiltered ? (
        // ── Home view: curated rail + category rows ─────────────────────────
        homeLoading ? (
          <div className="space-y-6">
            {[...Array(3)].map((_, i) => (
              <div key={i}>
                <div className="h-4 w-32 bg-gray-200 dark:bg-gray-800 rounded mb-3 animate-pulse" />
                <div className="flex gap-3">
                  {[...Array(4)].map((_, j) => (
                    <div key={j} className="w-[140px] sm:w-[180px] aspect-square bg-gray-100 dark:bg-gray-800 rounded-lg animate-pulse flex-shrink-0" />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : sections.length === 0 ? (
          <EmptyGiftState onWhatsapp={whatsappHref} />
        ) : (
          <>
            <CuratedCollectionRail sections={sections} onOpenGift={setOpenGift} />
            {sections.map((s) => (
              <GiftCategoryRow key={s.category} category={s.category} gifts={s.gifts} total={s.total} onOpenGift={setOpenGift} />
            ))}
          </>
        )
      ) : (
        // ── Filtered view: flat grid (category chip or search active) ───────
        <div>
          {gridLoading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="aspect-square bg-gray-100 dark:bg-gray-800 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : gifts.length === 0 ? (
            <EmptyGiftState searchTerm={searchQuery} onWhatsapp={whatsappHref} />
          ) : (
            <>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3 md:grid-cols-4">
                {gifts.map((gift) => (
                  <GiftCard key={gift._id} gift={gift} onOpen={setOpenGift} variant="grid" />
                ))}
              </div>

              {pagination.pages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-6">
                  <button
                    onClick={() => fetchGrid(pagination.page - 1)}
                    disabled={pagination.page <= 1}
                    className="px-3 py-1.5 text-sm rounded-lg text-gray-600 dark:text-gray-300 disabled:opacity-30 hover:bg-gray-100 dark:hover:bg-gray-800 transition"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    Page {pagination.page} of {pagination.pages}
                  </span>
                  <button
                    onClick={() => fetchGrid(pagination.page + 1)}
                    disabled={pagination.page >= pagination.pages}
                    className="px-3 py-1.5 text-sm rounded-lg text-gray-600 dark:text-gray-300 disabled:opacity-30 hover:bg-gray-100 dark:hover:bg-gray-800 transition"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {openGift && <GiftDetailModal gift={openGift} onClose={() => setOpenGift(null)} />}
    </div>
  );
}

function EmptyGiftState({ searchTerm, onWhatsapp }) {
  return (
    <div className="flex flex-col items-center text-center px-4 py-16">
      <Gift className="w-8 h-8 text-purple-400 mb-3" />
      <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
        {searchTerm ? `We don't have "${searchTerm}" right now` : "This gift is not available on the site right now"}
      </h3>
      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1.5 max-w-sm leading-relaxed">
        Contact admin on WhatsApp and we'll sort out a special order for you directly.
      </p>
      <a
        href={onWhatsapp(searchTerm)}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 mt-5 px-5 py-2.5 bg-[#25D366] hover:bg-[#1ebe5a] text-white text-sm font-semibold rounded-xl transition shadow-sm"
      >
        <MessageCircle className="w-4 h-4" /> Chat Admin on WhatsApp
      </a>
    </div>
  );
}
